import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { analyticsAPI } from '../services/api';
import { AnalyticsOverview, APIError } from '../types';
import AreaChart from '../components/charts/AreaChart';
import BarChart from '../components/charts/BarChart';
import HourStrip from '../components/charts/HourStrip';
import BarList from '../components/charts/BarList';
import type { AreaChartDatum } from '../components/charts/AreaChart';
import type { BarChartDatum } from '../components/charts/BarChart';
import type { HourStripDatum } from '../components/charts/HourStrip';
import type { BarListItem } from '../components/charts/BarList';

/**
 * AnalyticsDashboard — superadmin usage overview.
 *
 * Rendered by AdminPanel BELOW its own `page-hero` band, so this component
 * deliberately ships body content only: no second hero, no page padding and no
 * max-width wrapper (Layout's <main> + AdminPanel already supply both).
 */

const RANGE_OPTIONS: number[] = [7, 30, 90];

type Tint = 'gold' | 'navy' | 'maroon';

const TINTS: Record<Tint, { bg: string; fg: string }> = {
  gold: { bg: 'bg-bmlc-gold-100', fg: 'text-bmlc-gold-600' },
  navy: { bg: 'bg-bmlc-navy-100', fg: 'text-bmlc-navy-600' },
  maroon: { bg: 'bg-bmlc-maroon-50', fg: 'text-bmlc-maroon-500' },
};

/* --- formatting helpers ---------------------------------------------------- */

const nf = (n: number): string =>
  Number.isFinite(n) ? Math.round(n).toLocaleString('en-IN') : '0';

const nf1 = (n: number): string => (Number.isFinite(n) ? n.toFixed(1) : '0.0');

/**
 * Parses a 'YYYY-MM-DD' calendar date as LOCAL midnight. `new Date(str)` would
 * read it as UTC and shift the label by a day in western timezones.
 */
const parseYmd = (value: string): Date | null => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value || '');
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? null : d;
};

/** '02 Aug' — compact axis / row label for a calendar date. */
const shortDay = (value: string): string => {
  const d = parseYmd(value);
  if (!d) return value;
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
};

/** '02 Aug 2026' — full date for prose (accepts ISO timestamps too). */
const longDate = (value: string): string => {
  const d = parseYmd(value) || new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const isSameDay = (a: Date, b: Date): boolean =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

/** 'Today' / '02 Aug' / 'Never' — the tight sub-line under a BarList value. */
const lastActiveLabel = (value: string | null): string => {
  if (!value) return 'Never';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return 'Never';
  if (isSameDay(d, new Date())) return 'Today';
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
};

/* --- small presentational pieces ------------------------------------------- */

const CardHead: React.FC<{
  title: string;
  tint: Tint;
  icon: React.ReactNode;
  aside?: React.ReactNode;
}> = ({ title, tint, icon, aside }) => (
  <div className="mb-3 flex items-start justify-between gap-3 sm:mb-4">
    <div className="flex min-w-0 items-center gap-2.5">
      <div
        className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg sm:h-8 sm:w-8 ${TINTS[tint].bg}`}
      >
        <svg
          className={`h-3.5 w-3.5 sm:h-4 sm:w-4 ${TINTS[tint].fg}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          {icon}
        </svg>
      </div>
      <h2 className="section-title mb-0 pb-2 text-base sm:text-lg">{title}</h2>
    </div>
    {aside ? <div className="flex-shrink-0 pt-0.5">{aside}</div> : null}
  </div>
);

const ICONS = {
  login: (
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1"
    />
  ),
  question: (
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  ),
  users: (
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
    />
  ),
  cap: (
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 14l9-5-9-5-9 5 9 5z M12 14l6.16-3.422A12.083 12.083 0 0112 20.055a12.083 12.083 0 01-6.16-9.477L12 14z"
    />
  ),
  chart: (
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
    />
  ),
  clock: (
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  ),
  trophy: (
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
    />
  ),
  refresh: (
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
    />
  ),
  warning: (
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
    />
  ),
};

const DashboardSkeleton: React.FC = () => (
  <div className="space-y-4 sm:space-y-5" aria-busy="true" aria-live="polite">
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 lg:grid-cols-4">
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="stat-card">
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="skeleton h-11 w-11 flex-shrink-0 rounded-full sm:h-12 sm:w-12" />
            <div className="min-w-0 flex-1 space-y-2">
              <div className="skeleton h-7 w-20 rounded-lg" />
              <div className="skeleton h-3 w-28 rounded" />
            </div>
          </div>
        </div>
      ))}
    </div>
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 sm:gap-5">
      {[0, 1].map((i) => (
        <div key={i} className="card p-4 sm:p-6">
          <div className="skeleton mb-4 h-5 w-40 rounded" />
          <div className="skeleton h-[220px] w-full rounded-xl" />
        </div>
      ))}
    </div>
    <div className="card p-4 sm:p-6">
      <div className="skeleton mb-4 h-5 w-32 rounded" />
      <div className="skeleton h-[78px] w-full rounded-xl" />
    </div>
    <div className="card p-4 sm:p-6">
      <div className="skeleton mb-4 h-5 w-48 rounded" />
      <div className="space-y-2">
        {[0, 1, 2, 3, 4].map((i) => (
          <div key={i} className="skeleton h-14 w-full rounded-xl" />
        ))}
      </div>
    </div>
    <p className="pt-1 text-center text-xs text-gray-400 sm:text-sm">Loading analytics...</p>
  </div>
);

/* --- page ------------------------------------------------------------------ */

const AnalyticsDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [days, setDays] = useState<number>(30);
  const [data, setData] = useState<AnalyticsOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Generation counter: switching range fires a new request while the previous
  // one may still be in flight, and the wider window is the slower query — only
  // the newest request is allowed to write state.
  const requestIdRef = useRef(0);

  const fetchOverview = useCallback(async (rangeDays: number) => {
    const requestId = ++requestIdRef.current;
    try {
      setLoading(true);
      setError('');
      const response = await analyticsAPI.getOverview(rangeDays);
      if (requestId !== requestIdRef.current) return;
      setData(response);
    } catch (err: any) {
      if (requestId !== requestIdRef.current) return;
      const apiError = err.response?.data as APIError;
      setError(apiError?.error || 'Failed to load analytics');
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    fetchOverview(days);
  }, [days, fetchOverview]);

  const summary = data ? data.summary : null;

  const daily = useMemo(
    () => (data && Array.isArray(data.daily) ? data.daily : []),
    [data]
  );

  const loginSeries = useMemo<AreaChartDatum[]>(
    () => daily.map((d) => ({ label: shortDay(d.date), value: d.logins })),
    [daily]
  );

  const questionSeries = useMemo<BarChartDatum[]>(
    () => daily.map((d) => ({ label: shortDay(d.date), value: d.questions })),
    [daily]
  );

  const hourSeries = useMemo<HourStripDatum[]>(() => {
    const hourly = data && Array.isArray(data.hourly) ? data.hourly : [];
    return hourly.map((h) => ({ hour: h.hour, value: h.questions }));
  }, [data]);

  const topItems = useMemo<BarListItem[]>(() => {
    const students = data && Array.isArray(data.top_students) ? data.top_students : [];
    return students.map((s) => ({
      id: s.id,
      label: s.username,
      sublabel: s.phone_number || undefined,
      value: s.questions_in_range,
      secondary: lastActiveLabel(s.last_active_at),
    }));
  }, [data]);

  /** Null when tracking predates the window — the chart then needs no caveat. */
  const trackingNote = useMemo<string | null>(() => {
    if (!data) return null;
    if (!data.login_tracking_since) {
      return 'Login tracking just enabled — data will build up from today.';
    }
    const since = new Date(data.login_tracking_since);
    if (Number.isNaN(since.getTime())) return null;
    const start = parseYmd(data.range ? data.range.start_date : '');
    if (start && since.getTime() <= start.getTime()) return null;
    return `Login tracking started ${longDate(data.login_tracking_since)}`;
  }, [data]);

  const kpis = useMemo(() => {
    const s = summary;
    return [
      {
        key: 'logins-today',
        label: 'Logins Today',
        value: nf(s ? s.logins_today : 0),
        sub: `${nf(s ? s.unique_logins_today : 0)} students`,
        tint: 'gold' as Tint,
        icon: ICONS.login,
      },
      {
        key: 'questions-today',
        label: 'Questions Today',
        value: nf(s ? s.questions_today : 0),
        sub: `${nf(s ? s.questions_in_range : 0)} in this period`,
        tint: 'navy' as Tint,
        icon: ICONS.question,
      },
      {
        key: 'active-today',
        label: 'Active Students Today',
        value: nf(s ? s.active_students_today : 0),
        sub: `${nf(s ? s.active_students_in_range : 0)} active this period`,
        tint: 'maroon' as Tint,
        icon: ICONS.users,
      },
      {
        key: 'total-students',
        label: 'Total Students',
        value: nf(s ? s.total_students : 0),
        sub: `+${nf(s ? s.new_students : 0)} new this period`,
        tint: 'navy' as Tint,
        icon: ICONS.cap,
      },
    ];
  }, [summary]);

  const neverActive = summary ? summary.never_active_students : 0;

  const rangeLabel = useMemo(() => {
    if (!data || !data.range) return `Last ${days} days`;
    return `${longDate(data.range.start_date)} – ${longDate(data.range.end_date)}`;
  }, [data, days]);

  /* --- header band (control bar; AdminPanel owns the navy hero) ----------- */
  const header = (
    <div className="card-premium mb-4 p-4 sm:mb-5 sm:p-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
        <div className="min-w-0">
          {/* h2, not h1 — AdminPanel's navy hero owns the page's single h1 */}
          <h2 className="font-serif text-xl font-bold text-bmlc-navy-900 sm:text-2xl">
            Analytics Dashboard
          </h2>
          <p className="mt-0.5 text-[11px] font-semibold tracking-wide text-bmlc-gold-600 sm:text-xs">
            Student engagement across {rangeLabel}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-1.5 sm:flex-nowrap sm:gap-2">
          <div
            className="flex items-center gap-1 rounded-xl bg-bmlc-navy-50 p-1"
            role="group"
            aria-label="Select reporting range"
          >
            {RANGE_OPTIONS.map((option) => {
              const active = option === days;
              return (
                <button
                  key={option}
                  type="button"
                  onClick={() => setDays(option)}
                  aria-pressed={active}
                  className={`tab-premium px-2.5 py-1.5 text-xs sm:px-3.5 sm:text-sm ${
                    active ? 'tab-premium-active' : ''
                  }`}
                >
                  {option}
                  <span className="hidden sm:inline"> days</span>
                  <span className="sm:hidden">d</span>
                </button>
              );
            })}
          </div>
          <button
            type="button"
            onClick={() => fetchOverview(days)}
            disabled={loading}
            className="btn-ghost flex-shrink-0 rounded-lg p-2 disabled:cursor-not-allowed disabled:opacity-50 sm:p-2.5"
            title="Refresh analytics"
            aria-label="Refresh analytics"
          >
            <svg
              className={`h-4 w-4 sm:h-5 sm:w-5 ${loading ? 'animate-spin' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              {ICONS.refresh}
            </svg>
          </button>
        </div>
      </div>
    </div>
  );

  const errorBanner = error ? (
    <div className="toast-error mb-4 sm:mb-5">
      <svg
        className="h-4 w-4 flex-shrink-0 sm:h-5 sm:w-5"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        {ICONS.warning}
      </svg>
      <p className="min-w-0 flex-1 text-xs sm:text-sm">{error}</p>
      <button
        type="button"
        onClick={() => fetchOverview(days)}
        disabled={loading}
        className="btn-danger-soft flex-shrink-0 px-3 py-1.5 text-xs font-semibold disabled:cursor-not-allowed disabled:opacity-60 sm:text-sm"
      >
        Retry
      </button>
    </div>
  ) : null;

  /* --- initial load / hard failure ---------------------------------------- */
  if (loading && !data) {
    return (
      <div className="animate-fade-in">
        {header}
        {errorBanner}
        <DashboardSkeleton />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="animate-fade-in">
        {header}
        {errorBanner}
        <div className="card px-4 py-10 text-center sm:py-14">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-bmlc-gold-50 ring-1 ring-bmlc-gold-200 sm:h-20 sm:w-20">
            <svg
              className="h-8 w-8 text-bmlc-gold-500 sm:h-10 sm:w-10"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              {ICONS.chart}
            </svg>
          </div>
          <p className="mb-1 font-serif text-base font-semibold text-bmlc-navy-800 sm:text-lg">
            Analytics unavailable
          </p>
          <p className="text-xs text-gray-400 sm:text-sm">
            We could not load usage data for this period.
          </p>
        </div>
      </div>
    );
  }

  /* --- loaded ------------------------------------------------------------- */
  const secondary = [
    {
      key: 'questions-range',
      label: 'Questions in Period',
      value: nf(summary ? summary.questions_in_range : 0),
      note: `${nf(summary ? summary.total_questions : 0)} all time`,
      danger: false,
    },
    {
      key: 'avg-per-student',
      label: 'Avg / Active Student',
      value: nf1(summary ? summary.avg_questions_per_active_student : 0),
      note: `${nf(summary ? summary.active_students_in_range : 0)} active students`,
      danger: false,
    },
    {
      key: 'never-active',
      label: 'Never Active',
      value: nf(neverActive),
      note: neverActive > 0 ? 'Have never asked a question' : 'Everyone has engaged',
      danger: neverActive > 0,
    },
    {
      key: 'unique-logins',
      label: 'Unique Logins',
      value: nf(summary ? summary.unique_logins_in_range : 0),
      note: `${nf(summary ? summary.logins_in_range : 0)} sign-ins in period`,
      danger: false,
    },
  ];

  return (
    <div className="animate-fade-in">
      {header}
      {errorBanner}

      <div className={loading ? 'opacity-60 transition-opacity' : 'transition-opacity'}>
        {/* KPI row */}
        <div className="mb-4 grid grid-cols-1 gap-3 sm:mb-5 sm:grid-cols-2 sm:gap-4 lg:grid-cols-4">
          {kpis.map((kpi) => (
            <div key={kpi.key} className="stat-card animate-slide-up">
              <div className="flex items-center gap-3 sm:gap-4">
                <div
                  className={`flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full sm:h-12 sm:w-12 ${
                    TINTS[kpi.tint].bg
                  }`}
                >
                  <svg
                    className={`h-5 w-5 sm:h-6 sm:w-6 ${TINTS[kpi.tint].fg}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    {kpi.icon}
                  </svg>
                </div>
                <div className="min-w-0">
                  <p className="stat-value">{kpi.value}</p>
                  <p className="stat-label">{kpi.label}</p>
                  <p className="mt-1 truncate text-[11px] text-gray-500">{kpi.sub}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Charts */}
        <div className="mb-4 grid grid-cols-1 gap-4 sm:mb-5 md:grid-cols-2">
          <div className="card p-4 sm:p-6">
            <CardHead
              title="Daily Logins"
              tint="gold"
              icon={ICONS.login}
              aside={
                <span className="badge-navy font-sans">
                  {nf(summary ? summary.logins_in_range : 0)}
                </span>
              }
            />
            <div className="mb-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-gray-500">
              <span className="inline-flex items-center gap-1.5">
                <span className="inline-block h-0.5 w-4 rounded-full bg-bmlc-navy-500" />
                Sign-ins per day
              </span>
              <span className="text-gray-400">
                {nf(summary ? summary.unique_logins_in_range : 0)} unique students signed in
              </span>
            </div>
            <AreaChart data={loginSeries} color="navy" valueLabel="Logins" />
            {trackingNote ? (
              <p className="mt-2 flex items-start gap-1.5 text-[11px] italic text-gray-400">
                <svg
                  className="mt-px h-3.5 w-3.5 flex-shrink-0 text-bmlc-gold-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <span>{trackingNote}</span>
              </p>
            ) : null}
          </div>

          <div className="card p-4 sm:p-6">
            <CardHead
              title="Daily Questions"
              tint="navy"
              icon={ICONS.chart}
              aside={
                <span className="badge-navy font-sans">
                  {nf(summary ? summary.questions_in_range : 0)}
                </span>
              }
            />
            <div className="mb-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-gray-500">
              <span className="inline-flex items-center gap-1.5">
                <span className="inline-block h-2 w-2 rounded-sm bg-bmlc-navy-500" />
                Questions asked per day
              </span>
            </div>
            <BarChart data={questionSeries} color="navy" />
          </div>
        </div>

        {/* Peak hours */}
        <div className="card mb-4 p-4 sm:mb-5 sm:p-6">
          <CardHead
            title="Peak Hours"
            tint="maroon"
            icon={ICONS.clock}
            aside={
              <span className="hidden text-[11px] text-gray-400 sm:inline">
                Questions by hour of day
              </span>
            }
          />
          <HourStrip data={hourSeries} />
        </div>

        {/* Secondary summary strip */}
        <div className="mb-4 grid grid-cols-2 gap-3 sm:mb-5 sm:gap-4 lg:grid-cols-4">
          {secondary.map((item) => (
            // `stat-card` (an @layer components class) is used instead of `card`
            // so the maroon utility overrides below actually win the cascade —
            // plain `.card` is declared after @tailwind utilities in index.css.
            <div
              key={item.key}
              className={`stat-card p-3.5 sm:p-4 ${
                item.danger ? 'border-bmlc-maroon-500/25 bg-bmlc-maroon-50/60' : ''
              }`}
            >
              <p className="stat-label mt-0 truncate">{item.label}</p>
              <p className="mt-1.5 flex items-baseline gap-2">
                <span
                  className={`font-serif text-2xl font-bold leading-none sm:text-3xl ${
                    item.danger ? 'text-bmlc-maroon-500' : 'text-bmlc-navy-900'
                  }`}
                >
                  {item.value}
                </span>
                {item.danger ? (
                  <span className="badge-danger flex-shrink-0">Attention</span>
                ) : null}
              </p>
              <p className="mt-1.5 truncate text-[11px] text-gray-500">{item.note}</p>
            </div>
          ))}
        </div>

        {/* Most active students */}
        <div className="card p-4 sm:p-6">
          <CardHead
            title="Most Active Students"
            tint="gold"
            icon={ICONS.trophy}
            aside={
              topItems.length > 0 ? (
                <span className="badge-gold font-sans">Top {topItems.length}</span>
              ) : null
            }
          />
          <p className="mb-3 text-[11px] text-gray-400">
            Ranked by questions asked in this period — select a student to open Chat Monitoring.
          </p>
          <BarList
            items={topItems}
            onItemClick={(id) => navigate(`/admin/chats?student=${id}`)}
          />
        </div>
      </div>
    </div>
  );
};

export default AnalyticsDashboard;
